import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useOrganizationList, useUser } from '@clerk/clerk-react';

import { authStore } from '../../stores/AuthStore';
import { projectService } from '../../services/ProjectService';
import { rootStore } from '../../stores/RootStore';
import { toast } from 'sonner';

const PendingPromptHandler = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isLoaded: isUserLoaded } = useUser();
    const { userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList();
    const { uiStore } = rootStore;
    const [retryCount, setRetryCount] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const MAX_RETRIES = 10;
    const RETRY_INTERVAL = 2000;
    const processingRef = useRef(false);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const handlePendingPrompt = async () => {
            // Prevent concurrent processing
            if (processingRef.current) {
                console.log('[PendingPromptHandler] Already processing, skipping');
                return;
            }

            console.log('[PendingPromptHandler] Checking conditions:', {
                isUserLoaded,
                hasUser: !!user,
                isTokenSet: authStore.isTokenSet,
                currentPath: location.pathname,
                hasOrg: !!user?.organizationMemberships?.length,
                retryCount,
                isProcessing,
                isOrgListLoaded,
                orgListLength: userMemberships?.count
            });

            // Basic validation
            if (!isUserLoaded || !user || !authStore.isTokenSet || !isOrgListLoaded) {
                console.log('[PendingPromptHandler] Basic conditions not met, returning');
                return;
            }

            // Check if we're on the right page
            if (location.pathname !== '/dashboard' && location.pathname !== '/sign-up/sso-callback') {
                console.log('[PendingPromptHandler] Not on dashboard or sign-up callback, current path:', location.pathname);
                return;
            }

            // Check for pending prompt
            const pendingPrompt = localStorage.getItem('pendingPrompt');
            const selectedModel = localStorage.getItem('selectedModel');
            if (!pendingPrompt) {
                console.log('[PendingPromptHandler] No pending prompt found');
                return;
            }

            console.log('[PendingPromptHandler] Found pending prompt:', !!pendingPrompt);

            // Handle organization membership
            if (!user.organizationMemberships?.length || !userMemberships?.count) {
                if (retryCount < MAX_RETRIES) {
                    console.log('[PendingPromptHandler] No organization membership yet, retrying...', {
                        retryCount,
                        maxRetries: MAX_RETRIES,
                        hasOrgMemberships: !!user.organizationMemberships?.length,
                        hasOrgList: !!userMemberships?.count
                    });
                    setRetryCount(prev => prev + 1);
                    timeoutId = setTimeout(handlePendingPrompt, RETRY_INTERVAL);
                    return;
                } else {
                    console.log('[PendingPromptHandler] Max retries reached, showing error');
                    toast.error('Failed to set up your organization. Please try again.');
                    localStorage.removeItem('pendingPrompt');
                    localStorage.removeItem('selectedModel');
                    return;
                }
            }

            try {
                processingRef.current = true;
                setIsProcessing(true);

                // Show loading toast
                const loadingToast = toast.loading('Creating your project...');

                // Get the first organization
                const firstMembership = userMemberships.data[0];
                if (!firstMembership?.organization) {
                    throw new Error('No organization found in list');
                }

                const org = firstMembership.organization;

                // Ensure user context is set in ProjectService
                console.log('[PendingPromptHandler] Setting user context:', {
                    userId: user.id,
                    orgId: org.id,
                    orgSlug: org.slug
                });
                projectService.setUserContext(user.id, org.id);

                // Create a new project with type 'prompt'
                console.log('[PendingPromptHandler] Creating project...');
                const project = await projectService.createProject(undefined, 'prompt');
                console.log('[PendingPromptHandler] Project created:', project);

                // Set the prompt and make agent visible
                uiStore.initialAgentPrompt = pendingPrompt;
                uiStore.setAgentVisible(true);

                // Store the selected model in localStorage for the agent panel
                if (selectedModel) {
                    localStorage.setItem('webwald-selected-model', selectedModel);
                }

                // Clear the pending prompt and model
                localStorage.removeItem('pendingPrompt');
                localStorage.removeItem('selectedModel');

                // Update loading toast to success
                toast.success('Project created successfully!', {
                    id: loadingToast
                });

                // Navigate to the project
                console.log('[PendingPromptHandler] Navigating to project:', {
                    orgSlug: org.slug,
                    projectId: project.id
                });

                navigate({
                    to: '/org/$orgSlug/$projectId',
                    params: {
                        orgSlug: org.slug,
                        projectId: project.id
                    }
                });
            } catch (error) {
                console.error('[PendingPromptHandler] Error handling pending prompt:', error);

                // Show specific error message based on the error
                if (error instanceof Error) {
                    if (error.message.includes('organization')) {
                        toast.error('Organization setup is incomplete. Please try again.');
                    } else {
                        toast.error(`Failed to create project: ${error.message}`);
                    }
                } else {
                    toast.error('Failed to create project. Please try again.');
                }

                // Clear the pending prompt on error to prevent infinite retries
                localStorage.removeItem('pendingPrompt');
                localStorage.removeItem('selectedModel');
            } finally {
                processingRef.current = false;
                setIsProcessing(false);
            }
        };

        handlePendingPrompt();

        // Cleanup function
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isUserLoaded, user, authStore.isTokenSet, navigate, uiStore, location.pathname, retryCount, isOrgListLoaded, userMemberships]);

    return null;
};

export default PendingPromptHandler; 